import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Menu, X, User } from 'lucide-react';
import { useState } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';

const Navbar = () => {
  const { totalItems } = useCart();
  const { isAuthenticated } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/shop', label: 'Shop All' },
    { href: '/shop/makeup', label: 'Makeup' },
    { href: '/shop/jewelry', label: 'Jewelry' },
    { href: '/shop/body', label: 'Body' },
    { href: '/shop/nails', label: 'Nails' },
    { href: '/shop/hair', label: 'Hair' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="font-heading text-xl md:text-2xl tracking-tight"
            data-testid="logo"
          >
            <span className="text-gold">Unbothered</span>
            <span className="text-foreground ml-2 italic">Baddie</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className="text-sm font-body tracking-widest uppercase text-muted-foreground hover:text-gold transition-colors underline-animate"
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-4">
            {isAuthenticated && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin')}
                className="text-gold hover:text-gold-light"
                data-testid="admin-dashboard-btn"
              >
                <User className="h-5 w-5" />
              </Button>
            )}
            
            <Link 
              to="/cart" 
              className="relative p-2 hover:text-gold transition-colors"
              data-testid="cart-btn"
            >
              <ShoppingBag className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-gold text-black text-xs w-5 h-5 flex items-center justify-center font-medium">
                  {totalItems}
                </span>
              )}
            </Link>

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background border-l border-white/10 w-72">
                <SheetHeader>
                  <SheetTitle className="font-heading text-gold">Menu</SheetTitle>
                </SheetHeader>
                <div className="mt-8 flex flex-col space-y-4">
                  {navLinks.map(link => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileOpen(false)}
                      className="text-sm font-body tracking-widest uppercase text-muted-foreground hover:text-gold transition-colors py-2"
                    >
                      {link.label}
                    </Link>
                  ))}
                  <hr className="border-white/10 my-4" />
                  <Link
                    to="/admin/login"
                    onClick={() => setMobileOpen(false)}
                    className="text-xs font-body tracking-widest uppercase text-muted-foreground hover:text-gold transition-colors py-2"
                  >
                    Admin
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
